import { CompanyRoleDTO } from "@/types/CompanyRoleDTO";
import type { CookieApiResponse } from "@/types/responses/UserResponses";
import type { InviteSkipReason } from "./EventRoleClient";
import { CompanyRole } from "@/types/CompanyRole";
import type { DissolvePreflightKeys } from "@/components/PDeleteCompany/dissolveText";
import { lossesAckQuery, type LossesAck } from "@/lib/legal/dissolveLossesAck";
import { apiDelete, apiGet, apiPut, apiPost } from "../_apiShim";
const BASE_PATH = "/api/v1/company-roles/";

// Get company role by user ID and company ID
export async function getCompanyRoleByUserIdAndCompanyId(
  companyId: string,
  fields: string,
): Promise<CompanyRole> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";

  return await apiGet<CompanyRole>(
    `${BASE_PATH}${companyId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Create a company role
export async function createCompanyRole(
  companyId: string,
  email: string,
  role: string,
): Promise<string> {
  return await apiPost<string>(
    `${BASE_PATH}${companyId}`,
    {
      email: email,
      role: role,
    },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export type CompanyInviteOutcome = {
  requested: number;
  inserted: number;
  skipped: { email: string; reason: InviteSkipReason }[];
  /** Inserted addresses whose invite mail is not confirmed sent; the invitation stands. */
  mailNotConfirmed: string[];
};

/**
 * Members wave A2 — invite up to 50 addresses with one role
 * (`POST /company-roles/{companyId}/batch`). Every address comes back either
 * inserted or in `skipped`, in request order, as sent.
 */
export async function createCompanyRolesBatch(
  companyId: string,
  emails: string[],
  role: string,
): Promise<CompanyInviteOutcome> {
  return await apiPost<CompanyInviteOutcome>(
    `${BASE_PATH}${companyId}/batch`,
    { emails, role },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Update a company role
export async function updateCompanyRole(
  companyRole: CompanyRole,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${companyRole.companyId}`,
    {
      companyRoleId: companyRole.companyRoleId,
      role: companyRole.role,
      userId: companyRole.userId,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete a company role
export async function deleteCompanyRole(
  companyId: string,
  companyRoleId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${companyId}/${companyRoleId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Offboarding (plan §4.2 (5)): what removing this member also ends — their
 * valid and pending rows on the organisation's events and the events they own
 * (ownership moves to the organisation's owner). Company OWNER/ADMIN only.
 * `offboardingEnabled: false` (the backend's kill switch) comes with zeros;
 * an older backend answers 404.
 */
export type CompanyMemberRemovalPreview = {
  offboardingEnabled: boolean;
  /** Valid event rows that go. */
  eventCount: number;
  /** Pending event invitations that go. */
  pendingCount: number;
  ownedEventCount: number;
  /** At most 10, hex ids. */
  ownedEvents: { eventId: string; name: string }[];
};

export async function getCompanyMemberRemovalPreview(
  companyId: string,
  companyRoleId: string,
): Promise<CompanyMemberRemovalPreview> {
  return await apiGet<CompanyMemberRemovalPreview>(
    `${BASE_PATH}${companyId}/${companyRoleId}/removal-preview`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Update company association.
 *
 * PB-CDP: a solo owner's switch dissolves the current organisation, and the
 * door refuses a dissolve that forfeits purchased event plans or balances
 * (`lossesNotAcknowledged`) unless the caller acknowledges the join dialog's
 * loss notice — `&acknowledgeLosses=true` (`@QueryParam`, absent = false; an
 * old backend ignores it) and, PB-CDP-7, the ticked acknowledgement's
 * `lossesAck*` params after it. Sent only with one.
 */
export async function changeCompany(
  companyId: string,
  newCompanyId: string,
  lossesAck: LossesAck | null = null,
): Promise<CookieApiResponse> {
  const response = await apiPost<CookieApiResponse>(
    `${BASE_PATH}${companyId}/update-company?newCompanyId=${newCompanyId}${lossesAckQuery(lossesAck, "&")}`,
    {},
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  if (!response.companyId) {
    throw new Error("unknown");
  }

  return response;
}

// Switch owner
export async function switchOwner(
  companyId: string,
  newOwnerId: string,
): Promise<CookieApiResponse> {
  const response = await apiPost<CookieApiResponse>(
    `${BASE_PATH}${companyId}/switch-owner`,
    { newOwnerId },
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  if (!response.companyId) {
    throw new Error("unknown");
  }

  return response;
}

// Get valid paginated company roles
export async function getPaginatedCompanyRoles(
  companyId: string,
  page: number = 1,
  pageSize: number = 10,
): Promise<CompanyRoleDTO[]> {
  const queryParams = `?page=${encodeURIComponent(
    page,
  )}&pageSize=${encodeURIComponent(pageSize)}`;

  return await apiGet<CompanyRoleDTO[]>(
    `${BASE_PATH}${companyId}/company-paginated${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Picker-shaped search: q substring-matches email or display name. Empty q
// returns the first {limit} members so the picker can pre-populate before
// the operator types.
export async function searchCompanyRoles(
  companyId: string,
  q: string,
  limit: number = 20,
): Promise<CompanyRoleDTO[]> {
  const queryParams = `?q=${encodeURIComponent(q)}&limit=${encodeURIComponent(limit)}`;
  return await apiGet<CompanyRoleDTO[]>(
    `${BASE_PATH}${companyId}/search${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Get invalid paginated company roles by user
export async function getInvalidPaginatedCompanyRolesByUserId(
  fields: string,
  page: number = 1,
  pageSize: number = 10,
): Promise<CompanyRole[]> {
  const queryParams = `?fields=${encodeURIComponent(
    fields,
  )}&page=${encodeURIComponent(page)}&pageSize=${encodeURIComponent(pageSize)}`;

  return await apiGet<CompanyRole[]>(
    `${BASE_PATH}invalid-for-user${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Get join preflight check
export async function getJoinPreflight(
  companyId: string,
): Promise<JoinPreflightResponse> {
  return await apiGet<JoinPreflightResponse>(
    `${BASE_PATH}${companyId}/join-preflight`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * PB-CDP: on a solo owner's preflight (`willDeleteCompany` — the switch
 * dissolves the organisation) a new backend adds the dissolve gate's keys
 * `dissolveCheckFailed` (the new-backend marker) / `dissolveBlock` /
 * `dissolveLosses`; an old backend, a non-owner's and a blocked owner's answer
 * have none. Read them with `describeDissolve`. `hasActiveSubscriptions` stays
 * the legacy read (true for one-time event plans too) — not a dissolve gate.
 */
export type JoinPreflightResponse = {
  willDeleteCompany: boolean;
  hasActiveSubscriptions: boolean;
  blocked: boolean;
  blockedReason: string;
  /**
   * Offboarding (MED-5): the backend's kill switch. Absent (older backend) =
   * off, and `eventsLost` is then ignored.
   */
  offboardingEnabled?: boolean;
  /**
   * Valid event rows on the current organisation's events that joining
   * another one ends (decision Q1). Non-owner callers only.
   */
  eventsLost?: number;
} & Partial<DissolvePreflightKeys>;

export async function declineCompanyRole(
  companyId: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}/declineCompanyRole?companyId=${companyId}`,
    {},
    {
      "Cache-Control": "no-store",
    },
  );
}
